import { removeTokens, saveTokens } from '../auth';
import { collectCarryThroughParams } from '../carryThroughTracking';
import { CAPTCHA_KEY, getCaptchaToken } from '../iframe/captchaHelper';
import { clearApiClient } from './clearApiClient';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

export const REFRESH_TOKEN_ROUTE = '/web/v1/account/refresh';

/**
 * Sends refresh token request and saves tokens to Cookies
 * @param {string} refreshToken
 */
export const sendTokenRefreshRequest = (refreshToken: string) =>
  clearApiClient.post<TokenResponse>(REFRESH_TOKEN_ROUTE, { refresh_token: refreshToken }).then(
    (response) => {
      saveTokens(response.data.access_token, response.data.refresh_token);
    },
    (err: unknown) => {
      throw err;
    },
  );

export const PERM_TOKEN_ROUTE = '/web/v1/account/sign-in/perm-token';

/**
 * Exchanges `permanent_token` for a pair of regular ones (`access_token` and `refresh_token`) and saves them to Cookies
 * @param {string} permanentToken
 */
export const sendPermTokenRequest = (permanentToken: string) =>
  clearApiClient.post<TokenResponse>(PERM_TOKEN_ROUTE, { perm_token: permanentToken }).then((response) => {
    saveTokens(response.data.access_token, response.data.refresh_token);
  });

/**
 * Creates a permanent auth token for the current user
 * @returns permanent token, that can be exchanged via `/web/v1/account/sign-in/perm-token`
 */
export const sendCreatePermanentTokenRequest = (): Promise<string> =>
  clearApiClient
    .post<{ perm_token: string }>('/web/v1/account/session/perm-token')
    .then((response) => response.data.perm_token);

/**
 * Sends an email to the specified email address.
 * The email contains a 6-digit code and a magic link, that has these params encoded.
 */
export const sendOneTimeCodeEmailRequest = (email: string, selectedPlanSku?: string, code?: string) =>
  getCaptchaToken().then((captchaToken) =>
    clearApiClient.post<void, void>('/web/v1/account/one-time-code/request', {
      email,
      analytics: collectCarryThroughParams({
        code,
        plan: selectedPlanSku,
      }),
      recaptcha_token: captchaToken,
      recaptcha_site_key: CAPTCHA_KEY,
    }),
  );

/**
 * Verifies the code sent to the email address, saves tokens to Cookies.
 */
export const sendOneTimeCodeVerifyRequest = (email: string, code: string) =>
  getCaptchaToken().then((captchaToken) =>
    clearApiClient
      .post<TokenResponse>('/web/v1/account/one-time-code/verify', {
        email,
        code,
        recaptcha_token: captchaToken,
        recaptcha_site_key: CAPTCHA_KEY,
      })
      .finally(() => {
        removeTokens();
      })
      .then(
        (response) => {
          if (response.data.access_token) {
            saveTokens(response.data.access_token, response.data.refresh_token);
          } else {
            throw new Error('No access token in the response');
          }
        },
        (error: unknown) => {
          throw error;
        },
      ),
  );
